import { useCallback, useState } from 'react';
import type { LegalAcceptanceState } from '@lds-efsp/shared-contracts';
import { useApi, API_BASE_URL } from './useApi';

interface UseLegalApiReturn {
  getAcceptanceState: () => Promise<LegalAcceptanceState>;
  dismissTermsNotice: (
    termsVersion: number
  ) => Promise<{ dismissedAt: string; termsVersion: number }>;
  loading: boolean;
  error: string | null;
}

/**
 * Legal acceptance API calls (SOR-37): the recorded terms/privacy pair plus the
 * notice model's legs — the read that carries the server's notice verdict, and
 * the write that records the caller's dismissal of a Terms notice.
 *
 * The pair-recording POST has no client here. Its only caller was the blocking
 * re-acceptance interstitial, and the dismissal is the sole write the notice
 * model makes: nothing in the portal records an acceptance any more, because
 * continued use after a version's effective date IS the acceptance. The
 * endpoint itself stays on the api-gateway (registration acceptance never came
 * through it, and it is the pair's one writer if a surface ever needs one
 * again); leaving a client helper with no caller would have left a live write
 * to it behind a surface that no longer exists.
 */
export function useLegalApi(): UseLegalApiReturn {
  const { get, post } = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAcceptanceState = useCallback(async (): Promise<LegalAcceptanceState> => {
    setLoading(true);
    setError(null);
    try {
      return await get<LegalAcceptanceState>(`${API_BASE_URL}/api/legal/acceptance`);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to load legal acceptance';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [get]);

  const dismissTermsNotice = useCallback(
    async (termsVersion: number): Promise<{ dismissedAt: string; termsVersion: number }> => {
      setLoading(true);
      setError(null);
      try {
        return await post<{ dismissedAt: string; termsVersion: number }>(
          `${API_BASE_URL}/api/legal/notice-dismissal`,
          { termsVersion }
        );
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Failed to record notice dismissal';
        setError(message);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [post]
  );

  return { getAcceptanceState, dismissTermsNotice, loading, error };
}
